import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { inputClass } from '../components/ui/inputClass';
import { BrandMark } from '../components/ui/BrandMark';
import { ApiError } from '../lib/api';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const { login, continueAsGuest } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  function onGuest() {
    continueAsGuest();
    nav('/spots', { replace: true });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault(); setErr(null); setNeedsConfirm(false); setBusy(true);
    try {
      await login(email, password);
      nav(params.get('redirect') ?? '/dashboard', { replace: true });
    } catch (e) {
      const body = (e instanceof ApiError ? e.body : null) as { error?: string } | null;
      if (e instanceof ApiError && e.status === 403 && body?.error === 'EmailNotConfirmed') {
        setNeedsConfirm(true);
      } else if (e instanceof ApiError && e.status === 401) {
        setErr('Invalid email or password.');
      } else {
        setErr('Something went wrong. Please try again.');
      }
    }
    finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <BrandMark />
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-50">Welcome back</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Sign in to your Hookset account</p>
        </div>
        <form onSubmit={onSubmit} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 space-y-4 shadow-sm">
          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Email</label>
            <input
              id="email"
              className={inputClass}
              type="email"
              autoComplete="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              aria-invalid={err ? true : undefined}
              aria-describedby={err ? 'login-error' : undefined}
            />
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-slate-300">Password</label>
              <Link className="text-sm font-medium text-teal-600 dark:text-teal-400 hover:underline" to="/forgot-password">
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <input
                id="password"
                className={`${inputClass} pr-10`}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                aria-invalid={err ? true : undefined}
                aria-describedby={err ? 'login-error' : undefined}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors cursor-pointer"
              >
                {showPassword ? (
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                    <line x1="2" y1="2" x2="22" y2="22" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          {err && <p id="login-error" role="alert" className="text-sm text-red-500">{err}</p>}
          {needsConfirm && (
            <p role="alert" className="text-sm text-amber-600 dark:text-amber-400">
              Please confirm your email before signing in.{' '}
              <Link className="font-medium underline" to="/resend-confirmation">Resend confirmation</Link>
            </p>
          )}
          <button
            className="w-full rounded-lg bg-teal-600 dark:bg-teal-500 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 dark:hover:bg-teal-600 disabled:opacity-50 transition-colors cursor-pointer"
            disabled={busy}
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
            <span className="text-xs font-medium text-slate-400 dark:text-slate-500">OR</span>
            <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
          </div>

          <button
            type="button"
            onClick={onGuest}
            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 py-2.5 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
          >
            Continue as guest
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
          No account?{' '}
          <Link className="font-medium text-teal-600 dark:text-teal-400 hover:underline" to="/register">
            Register
          </Link>
        </p>
      </div>
    </div>
  );
}
